import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, OnInit, Type, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CmsContentItemContent } from '@coin/shared/cms/util';
import { AuthService } from '@coin/shared/auth/data-access';
import { CommunicationTypeEnum, ContentLanguage, EquityElementTypeEnum } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { CommunicationContent, CommunicationContentElements, Topic, TopicType } from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, EMPTY, Observable, switchMap } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { DateOperations } from '@coin/shared/core/util-helpers';
import { DynamicSpacerComponent } from '../../../equity/components/dynamic-spacer/dynamic-spacer.component';
import { EquityCardsComponent } from '../../../equity/components/equity-cards/equity-cards.component';
import { EquityElementNotFoundComponent } from '../../../equity/components/equity-element-not-found/equity-element-not-found.component';
import { EquityHeadlineComponent } from '../../../equity/components/equity-headline/equity-headline.component';
import { EquityMediaComponent } from '../../../equity/components/equity-media/equity-media.component';
import { EquitySubTitleComponent } from '../../../equity/components/equity-sub-title/equity-sub-title.component';
import { EquityTextComponent } from '../../../equity/components/equity-text/equity-text.component';
import { EquityTilesComponent } from '../../../equity/components/equity-tiles/equity-tiles.component';
import { EquityPageDataDirective } from '../../../equity/directives/equity-page-data.directive';
import { EquityPageComponentModel } from '../../../equity/models/equity-page-component.model';
import { TopicsState } from '../../../home/store';

@Component({
  selector: 'customer-app-news-popup',
  templateUrl: './news-popup.component.html',
  styleUrls: ['./news-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NewsPopupComponent implements OnInit {
  data = inject<{
    dto: Topic;
    title: string;
    description: string;
    type: TopicType;
  }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<NewsPopupComponent>>(MatDialogRef);
  private topicsState = inject(TopicsState);
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  authService = inject(AuthService);

  public allNews$: Observable<Topic[]> = toObservable(this.topicsState.topics).pipe(
    map(items => items.filter(item => item.type === CommunicationTypeEnum.News))
  );

  public newsTitle: string;
  public shownNews$: BehaviorSubject<Topic> = new BehaviorSubject(null);
  public filteredNewsList$: Observable<Topic[]> = null;
  public communicationTypeEnum = CommunicationTypeEnum;
  public cmsNewsItems: CmsContentItemContent = [] as never;
  public cmsData = false;

  @ViewChild(EquityPageDataDirective, { static: true }) equityPageDataHost: EquityPageDataDirective;

  ngOnInit(): void {
    this.newsTitle = this.data.title;
    this.shownNews$.next(this.data.dto);
    this.setFilteredNewsList();

    this.shownNews$
      .pipe(
        map(topic => this.getLanguageContent(topic)),
        tap(content => {
          this.newsTitle = content.title;
        }),
        map(content => this.getLanguageElements(content)),
        switchMap(data => {
          if (data.useCmsNews || (!data.useOldNews && data.cmsItemUsed)) {
            return this.http
              .get<
                string | Record<ContentLanguage, CmsContentItemContent>
              >(`${environment.cmsContentStageBucketCloudfront}${data.cmsItemUsed}`)
              .pipe(
                map(content =>
                  typeof content === 'string'
                    ? (JSON.parse(content) as Record<ContentLanguage, CmsContentItemContent>)
                    : content
                ),
                tap(file => {
                  this.cmsNewsItems = this.getCmsLanguageContent(file);
                  this.cmsData = true;
                })
              );
          }

          this.cmsData = false;
          this.loadComponents(data);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private setFilteredNewsList(): void {
    this.filteredNewsList$ = null;
    this.filteredNewsList$ = combineLatest([this.allNews$, this.shownNews$])
      .pipe(
        map(([news, currentNews]) => news.filter(item => item.id !== currentNews.id && !this.isOldNewsItem(item))),
        delay(100)
      )
      .pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isOldNewsItem(news: Topic): boolean {
    return news.done || (news.dateEnd && DateOperations.dueDateOverBasedOnDay(news.dateEnd));
  }

  private loadComponents(data: CommunicationContentElements): void {
    if (!data.elements?.length) {
      return;
    }
    this.equityPageDataHost.viewContainerRef.clear();
    for (const element of data.elements) {
      const matchingDomElement = this.getMatchingComponentInstance(element.type);
      this.appendElementToDom(matchingDomElement, element);
    }
  }

  private appendElementToDom(domElement: Type<unknown>, data: CommunicationContentElements['elements'][number]): void {
    const { viewContainerRef } = this.equityPageDataHost;
    const componentRef = viewContainerRef.createComponent(domElement);

    if (data) {
      (componentRef.instance as EquityPageComponentModel).data = data;
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  private getMatchingComponentInstance(type: EquityElementTypeEnum): Type<unknown> {
    switch (type) {
      case EquityElementTypeEnum.HEADLINE:
        return EquityHeadlineComponent;
      case EquityElementTypeEnum.SUB_TITLE:
        return EquitySubTitleComponent;
      case EquityElementTypeEnum.TEXT:
        return EquityTextComponent;
      case EquityElementTypeEnum.MEDIA:
        return EquityMediaComponent;
      case EquityElementTypeEnum.TILES:
        return EquityTilesComponent;
      case EquityElementTypeEnum.CARDS:
        return EquityCardsComponent;
      case EquityElementTypeEnum.SPACER:
        return DynamicSpacerComponent;
      default:
        return EquityElementNotFoundComponent;
    }
  }

  public setNews(news: Topic): void {
    this.shownNews$.next(news);
    this.setFilteredNewsList();
  }

  public getLanguageContent(news: Topic): CommunicationContent {
    const enContent = news?.contents?.find(item => item.language === ContentLanguage.ENGLISH);
    const enContentText = JSON.parse(enContent?.text || '{}');
    if (enContentText['useCmsNews'] || (!enContentText['useOldNews'] && enContentText['cmsItemUsed'])) {
      return enContent;
    }
    const foundContent: CommunicationContent = news?.contents?.find(
      item => item.language === this.translateService?.currentLang
    );
    return foundContent || news?.contents?.find(item => item.language === ContentLanguage.ENGLISH);
  }

  private getCmsLanguageContent(news: Record<ContentLanguage, CmsContentItemContent>): CmsContentItemContent {
    const foundContent = news[this.translateService?.currentLang as ContentLanguage];
    return foundContent || news[ContentLanguage.ENGLISH];
  }

  private getLanguageElements(content: CommunicationContent): CommunicationContentElements {
    return content?.text?.length ? JSON.parse(content.text) : { elements: [] };
  }
}
